import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown, ChevronUp, History, Trash2 } from "lucide-react";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import { getOptimizePackagePlan } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";

import { AlertV4, ButtonV4, FieldV4, ModalV4, SelectV4, TabsV4 } from "../common/ui";
import { pageItems } from "../common/ui/PaginationV4";

import {
  ACTIONS,
  BTN_ACTS,
  EMPTY_SALE_FILTER,
  SEQUENCE_NAME,
  type PickedState,
  type RunSequence,
  type SaleFilter,
  type SetProp,
  type State,
} from "../common/sequence/PackageV4";
import {
  ageRangeText,
  amountText,
  appointmentText,
  deleteBlockText,
  detailGroups,
  genderOf,
  historyOrders,
  historySummary,
  isComparable,
  isPaid,
  isUsedUp,
  lifespanText,
  optionText,
  orderableOf,
  orderItemGroups,
  PACKAGE_PAGE_SIZE,
  paidLineText,
  priceText,
  quantityText,
  selectedDateText,
  totalOf,
  unpaidOrderOf,
  usageLogsOf,
  usageText,
  type HistoryFilter,
  type MasterOption,
  type PackageItem,
  type PackageOrder,
  type PackageTabKey,
} from "./packageModelV4";

import "./_hisv4-package.scss";

// Types
type CardPackageV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  PackageV4Sequence?: State["PackageV4Sequence"];
  masterOptions?: Record<string, any>;
  patientId?: number;
  // callback
  runSequence: RunSequence;
  setProp?: SetProp;
  onGoAppointments?: () => void;
};

const HISTORY_CHIPS: { key: HistoryFilter; label: string }[] = [
  { key: "all", label: "ทั้งหมด" },
  { key: "left", label: "มีคงเหลือ" },
  { key: "done", label: "ใช้ครบแล้ว" },
];

const CardPackageV4 = (props: CardPackageV4Props) => {
  const seq = props.PackageV4Sequence;
  const orders = seq?.orders ?? [];
  const saleItems = seq?.saleItems ?? [];
  const serviceTypes: MasterOption[] = props.masterOptions?.packageServiceType ?? [];
  const packageTypes: MasterOption[] = props.masterOptions?.packageType ?? [];

  const [tab, setTab] = useState<PackageTabKey>("sale");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState<SaleFilter>(EMPTY_SALE_FILTER);
  const [detailOf, setDetailOf] = useState<PackageItem | null>(null);
  const [detailTab, setDetailTab] = useState<"items" | "info">("items");
  const [checked, setChecked] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [openCards, setOpenCards] = useState<number[]>([]);
  const [openLog, setOpenLog] = useState<number | null>(null);

  useEffect(() => {
    if (!props.patientId) return;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patientId,
    });
    setDraft(EMPTY_SALE_FILTER);
    setChecked([]);
    setCompareOpen(false);
    setMessage("");
  }, [props.patientId]);

  const checkedOrders = orders.filter((order) => checked.includes(order.id));
  const paidOrders = orders.filter(isPaid);
  const historyRows = historyOrders(orders, historyFilter);
  const page = seq?.page ?? 1;
  const saleTotal = seq?.saleTotal ?? 0;
  const pageCount = Math.max(1, Math.ceil(saleTotal / PACKAGE_PAGE_SIZE));
  const saleLoading = props.buttonLoadCheck?.[BTN_ACTS.SEARCH_SALE] === BLC_STATUS.LOADING;
  const orderLoading = props.buttonLoadCheck?.[BTN_ACTS.ORDER] === BLC_STATUS.LOADING;

  const optimize = seq?.optimize;
  const plan = useMemo(() => {
    if (!optimize?.packages.length) return null;
    const planOrders = orders.filter((order) => optimize.orderIds.includes(order.id));
    return getOptimizePackagePlan(planOrders as any, optimize.packages);
  }, [optimize, orders]);

  const errorKeys = [
    BTN_ACTS.SEARCH_SALE,
    BTN_ACTS.ADD,
    BTN_ACTS.LOAD_ORDERS,
    BTN_ACTS.OPTIMIZE,
    BTN_ACTS.ORDER,
  ];

  // Handler
  const handleTab = (key: PackageTabKey) => {
    setTab(key);
    setMessage("");
    for (const errorKey of [BTN_ACTS.ADD, BTN_ACTS.OPTIMIZE, BTN_ACTS.ORDER]) {
      if (props.errorMessage?.[errorKey]) props.setProp?.(`errorMessage.${errorKey}`, null);
    }
  };

  const handleSearch = (filter: SaleFilter, toPage = 1) => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SEARCH_SALE,
      filter,
      page: toPage,
    });
  };

  const handleClear = () => {
    setDraft(EMPTY_SALE_FILTER);
    handleSearch(EMPTY_SALE_FILTER);
  };

  const handlePage = (toPage: number) => {
    if (toPage < 1 || toPage > pageCount || toPage === page) return;
    handleSearch(seq?.filter ?? EMPTY_SALE_FILTER, toPage);
  };

  const handleOpenDetail = (item: PackageItem) => {
    setDetailOf(item);
    setDetailTab("items");
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.DETAIL, packageId: item.id });
  };

  const handleAdd = (item: PackageItem) => {
    if (unpaidOrderOf(orders, item.id)) {
      setMessage(`เลือก ${item.name} ไว้แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`);
      return;
    }
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.ADD,
      item,
      onDone: (added) =>
        setMessage(
          added
            ? `เลือก ${item.name} แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`
            : `เลือก ${item.name} ไว้แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`,
        ),
    });
  };

  const handleCheck = (order: PackageOrder, value: boolean) => {
    setChecked(value ? [...checked, order.id] : checked.filter((id) => id !== order.id));
    setCompareOpen(false);
  };

  const handleQty = (order: PackageOrder, step: number) => {
    const quantity = Number(order.quantity) + step;
    if (isPaid(order) || quantity < 1) return;
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.UPDATE_QTY, order, quantity });
  };

  const handleRemove = (order: PackageOrder) => {
    if (deleteBlockText(order)) return;
    setChecked(checked.filter((id) => id !== order.id));
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.REMOVE, order });
  };

  const handleCompare = () => {
    const next = !compareOpen;
    setCompareOpen(next);
    const orderIds = checkedOrders.filter(isComparable).map((order) => order.id);
    if (next && orderIds.length) {
      props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.OPTIMIZE, orderIds });
    }
  };

  const handleOrder = () => {
    const sendOrders = orderableOf(checkedOrders);
    if (!sendOrders.length) {
      setMessage("แพ็กเกจที่เลือกยังไม่ชำระเงินหรือไม่มีสิทธิ์คงเหลือ · สั่ง Order ไม่ได้");
      return;
    }
    setMessage("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.ORDER,
      orderIds: sendOrders.map((order) => order.id),
      onDone: () => {
        setChecked([]);
        setMessage(
          `ส่ง ${sendOrders.length} แพ็กเกจเข้า Order แล้ว · ยอด ${amountText(totalOf(sendOrders))} บาท`,
        );
      },
    });
  };

  const toggleCard = (id: number) => {
    setOpenCards(
      openCards.includes(id) ? openCards.filter((row) => row !== id) : [...openCards, id],
    );
  };

  // หัวหน้า
  const tabs: { key: PackageTabKey; n: number; label: string; hint: string }[] = [
    {
      key: "sale",
      n: 1,
      label: "แพ็กเกจที่เปิดขาย",
      hint: `เลือกแพ็กเกจที่ต้องการ (${saleTotal} รายการ)`,
    },
    {
      key: "selected",
      n: 2,
      label: "แพ็กเกจที่เลือก",
      hint: `ทำนัด · สั่ง Order (${orders.length} รายการ)`,
    },
    {
      key: "history",
      n: 3,
      label: "ประวัติและยอดคงเหลือ",
      hint: `ใช้สิทธิ์ · ดูคงเหลือ (${paidOrders.length} แพ็กเกจ)`,
    },
  ];

  const saleTab = (
    <section className="hv4-card hv4-pkg-panel">
      <div className="hv4-pkg-filters">
        <FieldV4 label="ค้นหาชื่อ / รหัส" htmlFor="hv4-pkg-q" className="hv4-pkg-f-q">
          <input
            id="hv4-pkg-q"
            className="hv4-input"
            placeholder="ค้นหาชื่อบริการ"
            value={draft.keyword}
            onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && handleSearch(draft)}
          />
        </FieldV4>
        <FieldV4 label="ประเภทบริการ" htmlFor="hv4-pkg-s" className="hv4-pkg-f-s">
          <SelectV4
            id="hv4-pkg-s"
            placeholder="ทั้งหมด"
            options={[{ value: "", text: "ทั้งหมด" }, ...serviceTypes]}
            value={draft.serviceType}
            onChange={(value) => setDraft({ ...draft, serviceType: value })}
          />
        </FieldV4>
        <FieldV4 label="ชนิด" htmlFor="hv4-pkg-k" className="hv4-pkg-f-k">
          <SelectV4
            id="hv4-pkg-k"
            placeholder="ทั้งหมด"
            options={[{ value: "", text: "ทั้งหมด" }, ...packageTypes]}
            value={draft.packageType}
            onChange={(value) => setDraft({ ...draft, packageType: value })}
          />
        </FieldV4>
        <button
          type="button"
          className="hv4-pkg-bt hv4-pkg-bt-search"
          disabled={saleLoading}
          onClick={() => handleSearch(draft)}
        >
          ค้นหา
        </button>
        <button type="button" className="hv4-pkg-bt hv4-pkg-bt-clear" onClick={handleClear}>
          เคลียร์
        </button>
      </div>

      <div className="hv4-pkg-scroll">
        <div className="hv4-pkg-table hv4-pkg-sale" role="table" aria-label="แพ็กเกจที่เปิดขาย">
          <div className="hv4-pkg-row hv4-pkg-head" role="row">
            <span role="columnheader">รหัส</span>
            <span role="columnheader">ชนิด</span>
            <span role="columnheader">ชื่อแพ็กเกจ (EN)</span>
            <span role="columnheader">ชื่อแพ็กเกจ (TH)</span>
            <span role="columnheader" className="hv4-pkg-c hv4-pkg-gender-h">
              เพศ
            </span>
            <span role="columnheader">ช่วงอายุ</span>
            <span role="columnheader" className="hv4-pkg-r">
              ราคา
            </span>
            <span role="columnheader" className="hv4-pkg-c">
              ระยะเวลา
            </span>
            <span role="columnheader" className="hv4-pkg-c hv4-pkg-icon-h">
              รายละเอียด
            </span>
            <span role="columnheader" className="hv4-pkg-c hv4-pkg-icon-h">
              เลือก
            </span>
          </div>
          {saleItems.length === 0 ? (
            <div className="hv4-pkg-empty" role="row">
              {saleLoading ? "กำลังโหลด" : "ไม่พบแพ็กเกจที่เปิดขายตามเงื่อนไข"}
            </div>
          ) : (
            saleItems.map((item) => {
              const gender = genderOf(item.gender);
              const inCart = !!unpaidOrderOf(orders, item.id);
              const adding =
                props.buttonLoadCheck?.[`${BTN_ACTS.ADD}_${item.id}`] === BLC_STATUS.LOADING;

              return (
                <div key={item.id} className="hv4-pkg-row" role="row">
                  <span role="cell">{item.code}</span>
                  <span role="cell">{optionText(packageTypes, item.package_type)}</span>
                  <span role="cell">{item.name_en || item.name}</span>
                  <span role="cell">{item.name}</span>
                  <span role="cell" className="hv4-pkg-c hv4-pkg-gender" title={gender.text}>
                    {gender.icon}
                  </span>
                  <span role="cell">{ageRangeText(item)}</span>
                  <span role="cell" className="hv4-pkg-r">
                    {priceText(item.price)}
                  </span>
                  <span role="cell" className="hv4-pkg-c">
                    {lifespanText(item)}
                  </span>
                  <span role="cell" className="hv4-pkg-c">
                    <button
                      type="button"
                      className="hv4-pkg-icon hv4-pkg-eye"
                      aria-label={`ดูรายละเอียด ${item.name}`}
                      onClick={() => handleOpenDetail(item)}
                    >
                      👁
                    </button>
                  </span>
                  <span role="cell" className="hv4-pkg-c">
                    <button
                      type="button"
                      className="hv4-pkg-icon hv4-pkg-buy"
                      aria-label={`เลือก ${item.name}`}
                      disabled={adding}
                      onClick={() => handleAdd(item)}
                    >
                      {inCart ? "✓" : "🛒"}
                    </button>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="hv4-pkg-pager">
        <span className="hv4-pkg-pager-text">
          {saleTotal
            ? `แสดง ${(page - 1) * PACKAGE_PAGE_SIZE + 1}–${Math.min(
                page * PACKAGE_PAGE_SIZE,
                saleTotal,
              )} จาก ${saleTotal} รายการ`
            : "ไม่พบรายการ"}
        </span>
        <span className="hv4-pkg-pager-pages">
          <button
            type="button"
            className="hv4-pkg-pg"
            aria-label="หน้าก่อนหน้า"
            onClick={() => handlePage(page - 1)}
          >
            ‹
          </button>
          {pageItems(page, pageCount).map((n, index) =>
            n === null ? (
              <span key={`gap-${index}`} className="hv4-pkg-pg-gap">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                className={`hv4-pkg-pg${n === page ? " hv4-pkg-pg--on" : ""}`}
                aria-current={n === page ? "page" : undefined}
                onClick={() => handlePage(n)}
              >
                {n}
              </button>
            ),
          )}
          <button
            type="button"
            className="hv4-pkg-pg"
            aria-label="หน้าถัดไป"
            onClick={() => handlePage(page + 1)}
          >
            ›
          </button>
        </span>
      </div>
    </section>
  );

  const compareBox = compareOpen && checked.length > 0 && (
    <div className="hv4-pkg-compare" role="status">
      <b>เปรียบเทียบความคุ้มของรายการที่เลือก</b>
      {checkedOrders
        .filter((order) => !isComparable(order))
        .map((order) => (
          <span key={order.id}>
            {order.product_name} · ราคาแพ็กเกจ ฿{priceText(order.price)} ·
            ไม่มีรายการที่เทียบราคาได้
          </span>
        ))}
      {checkedOrders.some(isComparable) &&
        (props.buttonLoadCheck?.[BTN_ACTS.OPTIMIZE] === BLC_STATUS.LOADING ? (
          <span>กำลังคำนวณ</span>
        ) : plan && plan.savings > 0 ? (
          <>
            {plan.packages.map((row) => (
              <span key={row.package.id}>
                {row.package.name} × {row.quantity} · ราคาแพ็กเกจ ฿{priceText(row.total_price)}
              </span>
            ))}
            <span>
              รายการที่เลือก ฿{priceText(plan.selectedTotal)} · ซื้อตามแพ็กเกจที่แนะนำ ฿
              {priceText(plan.total)} · ประหยัด ≈ ฿{priceText(plan.savings)}
            </span>
          </>
        ) : (
          seq?.optimize && <span>ไม่มีแพ็กเกจที่คุ้มกว่ารายการที่เลือก</span>
        ))}
    </div>
  );

  const selectedTab = (
    <section className="hv4-card hv4-pkg-panel">
      {orders.length === 0 ? (
        <div className="hv4-pkg-sel-empty">
          ยังไม่ได้เลือกแพ็กเกจ · ไปที่แท็บ "แพ็กเกจที่เปิดขาย" แล้วกดปุ่มรถเข็นที่แพ็กเกจที่ต้องการ
          <div>
            <button
              type="button"
              className="hv4-pkg-bt hv4-pkg-bt-go"
              onClick={() => handleTab("sale")}
            >
              ไปเลือกแพ็กเกจ
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="hv4-pkg-scroll">
            <div className="hv4-pkg-table hv4-pkg-sel" role="table" aria-label="แพ็กเกจที่เลือก">
              <div className="hv4-pkg-row hv4-pkg-head" role="row">
                <span role="columnheader" />
                <span role="columnheader">วันที่เลือก</span>
                <span role="columnheader">รหัส</span>
                <span role="columnheader">ชื่อแพ็กเกจ</span>
                <span role="columnheader" className="hv4-pkg-r">
                  ราคา
                </span>
                <span role="columnheader" className="hv4-pkg-c">
                  จำนวน
                </span>
                <span role="columnheader">การชำระเงิน</span>
                <span role="columnheader">วันที่นัดหมาย</span>
                <span role="columnheader">การใช้งาน</span>
                <span role="columnheader" />
              </div>
              {orders.map((order) => {
                const paid = isPaid(order);
                const blocked = deleteBlockText(order);
                const isChecked = checked.includes(order.id);

                return (
                  <div
                    key={order.id}
                    className={`hv4-pkg-row${isChecked ? " hv4-pkg-row--on" : ""}`}
                    role="row"
                  >
                    <span role="cell">
                      <input
                        type="checkbox"
                        className="hv4-pkg-check"
                        aria-label={`เลือก ${order.product_name}`}
                        checked={isChecked}
                        onChange={(e) => handleCheck(order, e.target.checked)}
                      />
                    </span>
                    <span role="cell">{selectedDateText(order)}</span>
                    <span role="cell">{order.product_code}</span>
                    <b role="cell">{order.product_name}</b>
                    <span role="cell" className="hv4-pkg-r">
                      {priceText(order.price)}
                    </span>
                    <span role="cell" className="hv4-pkg-qty">
                      <button
                        type="button"
                        aria-label="ลดจำนวน"
                        onClick={() => handleQty(order, -1)}
                      >
                        −
                      </button>
                      <span>{quantityText(order.quantity)}</span>
                      <button
                        type="button"
                        aria-label="เพิ่มจำนวน"
                        onClick={() => handleQty(order, 1)}
                      >
                        +
                      </button>
                    </span>
                    <b role="cell" className={paid ? "hv4-pkg-paid" : "hv4-pkg-unpaid"}>
                      {paid ? "ชำระแล้ว" : "รอชำระเงิน"}
                    </b>
                    <span role="cell">{appointmentText(order)}</span>
                    <span role="cell" className="hv4-pkg-usage">
                      {usageText(order)}
                    </span>
                    <span role="cell" className="hv4-pkg-c">
                      <button
                        type="button"
                        className="hv4-pkg-icon hv4-pkg-del"
                        title={blocked ?? `เอาออก ${order.product_name}`}
                        aria-label={blocked ?? `เอาออก ${order.product_name}`}
                        disabled={!!blocked}
                        onClick={() => handleRemove(order)}
                      >
                        <Trash2 size={22} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hv4-pkg-actions">
            <button
              type="button"
              className="hv4-pkg-bt hv4-pkg-bt-appt"
              onClick={() => props.onGoAppointments?.()}
            >
              ทำนัดหมาย
            </button>
            <button
              type="button"
              className="hv4-pkg-bt hv4-pkg-bt-order"
              disabled={!checked.length || orderLoading}
              onClick={handleOrder}
            >
              สั่ง Order
            </button>
            <button type="button" className="hv4-pkg-bt hv4-pkg-bt-compare" onClick={handleCompare}>
              คำนวณแพ็กเกจที่คุ้มกว่า
            </button>
            <div className="hv4-pkg-total">
              <b>ราคารวม</b>{" "}
              <span className="hv4-pkg-total-n">({checkedOrders.length} รายการ)</span>{" "}
              <b className="hv4-pkg-total-amt">{amountText(totalOf(checkedOrders))}</b> บาท
            </div>
          </div>
          {compareBox}
        </>
      )}
    </section>
  );

  const chipCount = (key: HistoryFilter) => historyOrders(orders, key).length;

  const historyTab = (
    <section className="hv4-pkg-hist">
      <div className="hv4-pkg-chips" role="group" aria-label="กรองตามสถานะ">
        {HISTORY_CHIPS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`hv4-pkg-chip${historyFilter === chip.key ? " hv4-pkg-chip--on" : ""}`}
            aria-pressed={historyFilter === chip.key}
            onClick={() => setHistoryFilter(chip.key)}
          >
            {chip.label} ({chipCount(chip.key)})
          </button>
        ))}
      </div>

      <div className="hv4-pkg-hist-list">
        {historyRows.length === 0 ? (
          <div className="hv4-card hv4-pkg-hist-empty">
            ยังไม่มีแพ็กเกจที่ชำระแล้ว · เลือกแพ็กเกจและชำระเงินก่อน
            จึงจะเริ่มใช้สิทธิ์และเห็นยอดคงเหลือได้
          </div>
        ) : (
          historyRows.map((order) => {
            const open = openCards.includes(order.id);
            const done = isUsedUp(order);

            return (
              <div key={order.id} className="hv4-card hv4-pkg-hcard">
                <button
                  type="button"
                  className="hv4-pkg-hcard-head"
                  aria-expanded={open}
                  aria-label={`${open ? "ย่อ" : "ขยาย"} ${order.product_name}`}
                  onClick={() => toggleCard(order.id)}
                >
                  <span className="hv4-pkg-chev">
                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                  <b className="hv4-pkg-hname">{order.product_name}</b>
                  <span className={`hv4-pkg-badge${done ? " hv4-pkg-badge--done" : ""}`}>
                    {done ? "ใช้ครบแล้ว" : "ใช้งานอยู่"}
                  </span>
                  <span className="hv4-pkg-hsum">{historySummary(order)}</span>
                </button>
                {open && (
                  <div className="hv4-pkg-hbody">
                    <div className="hv4-pkg-paidline">{paidLineText(order)}</div>
                    <div className="hv4-pkg-scroll-x">
                      <div className="hv4-pkg-items" role="table" aria-label={order.product_name}>
                        <div className="hv4-pkg-irow hv4-pkg-ihead" role="row">
                          <span role="columnheader">รายการ</span>
                          <span role="columnheader">สิทธิ์</span>
                          <span role="columnheader" className="hv4-pkg-c">
                            ใช้แล้ว
                          </span>
                          <span role="columnheader" className="hv4-pkg-c">
                            คงเหลือ
                          </span>
                          <span role="columnheader" className="hv4-pkg-c">
                            History
                          </span>
                        </div>
                        {orderItemGroups(order).map((group) => (
                          <React.Fragment key={group.category}>
                            <div className="hv4-pkg-icat">
                              <b>{group.category}</b> <span>({group.items.length} รายการ)</span>
                            </div>
                            {group.items.map((item) => {
                              const total = Number(item.quantity) || 0;
                              const left = Number(item.quantity_left) || 0;
                              const logOpen = openLog === item.id;
                              const logs = usageLogsOf(seq?.usageLogs ?? [], item.id);
                              const label = `${logOpen ? "ซ่อน" : "ดู"}ประวัติการใช้งาน ${item.product_name}`;

                              return (
                                <React.Fragment key={item.id}>
                                  <div className="hv4-pkg-irow" role="row">
                                    <span role="cell" className="hv4-pkg-iname">
                                      {item.product_name}
                                    </span>
                                    <span role="cell">{total} ครั้ง</span>
                                    <span role="cell" className="hv4-pkg-c">
                                      {total - left}
                                    </span>
                                    <b
                                      role="cell"
                                      className={`hv4-pkg-c ${left > 0 ? "hv4-pkg-left" : "hv4-pkg-zero"}`}
                                    >
                                      {left}
                                    </b>
                                    <span role="cell" className="hv4-pkg-c">
                                      <button
                                        type="button"
                                        className={`hv4-pkg-hist-btn${logOpen ? " hv4-pkg-hist-btn--on" : ""}`}
                                        title={label}
                                        aria-label={label}
                                        onClick={() => setOpenLog(logOpen ? null : item.id)}
                                      >
                                        <History size={22} />
                                      </button>
                                    </span>
                                  </div>
                                  {logOpen && (
                                    <div className="hv4-pkg-log">
                                      <b>ประวัติการใช้งาน · {item.product_name}</b>
                                      {logs.length === 0 ? (
                                        <span className="hv4-pkg-log-none">ยังไม่มีการใช้งาน</span>
                                      ) : (
                                        logs.map((log, index) => (
                                          <div key={index} className="hv4-pkg-log-line">
                                            <span className="hv4-pkg-log-at">{log.at}</span>
                                            <span>{log.text}</span>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );

  const detail = seq?.detail && seq.detail.id === detailOf?.id ? seq.detail : null;
  const detailLoading = props.buttonLoadCheck?.[BTN_ACTS.DETAIL] === BLC_STATUS.LOADING;
  const groups = detailGroups(detail);
  const infoRows = detailOf
    ? [
        ["รหัส", detailOf.code],
        ["ชนิด", optionText(packageTypes, detailOf.package_type) || "-"],
        ["ราคา", `${priceText(detailOf.price)} บาท`],
        ["ระยะเวลา", lifespanText(detailOf)],
        ["เพศ", genderOf(detailOf.gender).text],
        ["ช่วงอายุ", `${ageRangeText(detailOf)} ปี`],
        ["ประเภทบริการ", optionText(serviceTypes, detailOf.package_service_type) || "-"],
        ["ใช้ร่วมกันได้", "-"],
      ]
    : [];

  const detailModal = detailOf && (
    <ModalV4
      title={detailOf.name_en || detailOf.name}
      width={980}
      onClose={() => setDetailOf(null)}
    >
      <div className="hv4-pkg-modal">
        <div className="hv4-pkg-modal-sub">
          {[detailOf.name, detailOf.code, optionText(packageTypes, detailOf.package_type)]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <TabsV4<"items" | "info">
          ariaLabel="รายละเอียดแพ็กเกจ"
          items={[
            { key: "items", label: "รายการในแพ็กเกจ" },
            { key: "info", label: "ข้อมูลเพิ่มเติม" },
          ]}
          active={detailTab}
          onChange={setDetailTab}
        />
        <div className="hv4-pkg-modal-body">
          {detailTab === "items" ? (
            <>
              <div className="hv4-pkg-drow hv4-pkg-dhead">
                <span>รายการในแพ็กเกจ</span>
                <span>จำนวนสิทธิ์</span>
                <span>หน่วย</span>
              </div>
              <AlertV4 tone="danger" error={props.errorMessage?.[BTN_ACTS.DETAIL]} />
              {detailLoading ? (
                <div className="hv4-pkg-dempty">กำลังโหลด</div>
              ) : groups.length === 0 ? (
                <div className="hv4-pkg-dempty">
                  แผนนี้ได้ราคาสมาชิกและเครดิตตามที่กำหนด ไม่มีรายการสิทธิ์เพิ่มเติม
                </div>
              ) : (
                groups.map((group) => (
                  <div key={group.category} className="hv4-pkg-dcat">
                    <div className="hv4-pkg-dcat-title">
                      <b>{group.category}</b> <span>({group.items.length} รายการ)</span>
                    </div>
                    {group.items.map((item) => (
                      <div key={item.id} className="hv4-pkg-drow">
                        <span className="hv4-pkg-dname">
                          <b>{item.product_name}</b>
                          <small>
                            {[item.product_code, item.product_name_en].filter(Boolean).join(" · ")}
                          </small>
                        </span>
                        <span>{quantityText(item.quantity)}</span>
                        <span>{item.unit_name}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          ) : (
            <div className="hv4-pkg-info">
              {infoRows.map(([key, value]) => (
                <div key={key} className="hv4-pkg-info-row">
                  <span>{key}</span>
                  <b>{value}</b>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="hv4-pkg-modal-foot">
          <ButtonV4 onClick={() => setDetailOf(null)}>ปิดหน้าต่าง</ButtonV4>
        </div>
      </div>
    </ModalV4>
  );

  return (
    <div className="hv4-pkg">
      <h1 className="hv4-pkg-h1">Package</h1>
      <div className="hv4-pkg-steps" role="tablist" aria-label="ขั้นตอน Package">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={`hv4-pkg-step${tab === item.key ? " hv4-pkg-step--on" : ""}`}
            onClick={() => handleTab(item.key)}
          >
            <span className="hv4-pkg-step-n">{item.n}</span>
            <span className="hv4-pkg-step-text">
              <b>{item.label}</b>
              <small>{item.hint}</small>
            </span>
          </button>
        ))}
      </div>
      {message && (
        <div className="hv4-pkg-msg" role="status">
          {message}
        </div>
      )}
      {errorKeys.map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => props.setProp?.(`errorMessage.${key}`, null)}
        />
      ))}
      {tab === "sale" ? saleTab : tab === "selected" ? selectedTab : historyTab}
      {detailModal}
    </div>
  );
};

CardPackageV4.displayName = "CardPackageV4";

export default React.memo(CardPackageV4);
